import React, { useEffect, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";
import { Link } from "react-router-dom";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./AboutArtist.css";

/**
 * MEET ANJAAN — a photo-led, scroll-driven artist story.
 *
 * Uses the artwork and artist image URLs already referenced in your Cloudinary setup.
 * All images are visible independently of scroll reveal animation.
 * Existing hero video, text, and overall layout are preserved.
 */
const MEDIA = {
  heroVideo:
    "https://res.cloudinary.com/wbwvb4sk/video/upload/v1786904214/IMG_2320_dhq73k_1_1.mp4",
  artistPortrait:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792312881/WhatsApp_Image_2026-10-18_at_2.05.50_PM.jpg",
  // Existing Cloudinary assets from this page; replace with your chosen
  // Cloudinary artwork URLs as desired. No /public/artist-story folder needed.
  redPainting:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597543/Udaan_red_1.jpg",
  greenPainting:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1791960536/Untitled_design_8.png",
  bluePainting:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597547/uttejana_3.jpg",
};

// PHOTOGRAPHIC JOURNAL — genuine photographs only.
// Replace these image URLs with your best candid photos of Anjaan Vivek:
// 1. exhibition: artist speaking with visitors or viewing the work at an exhibition
// 2. studio: artist sketching / painting / working at an easel
// 3. installation: an atmospheric gallery installation photograph (optional)
// The two starter URLs already appear in your existing website's media history.
// Check the actual photograph before giving it a specific exhibition/studio caption.
// No fabricated or AI-generated artist photograph is used here.
const STORY_PHOTOS = {
  exhibition:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792314045/WhatsApp_Image_2026-10-18_at_2.29.09_PM.jpg",
  studio:
    "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792314579/WhatsApp_Image_2026-10-18_at_2.34.19_PM.jpg",
  installation: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792314433/WhatsApp_Image_2026-10-18_at_2.36.06_PM.jpg",
};

// Optional: add a separate Hindi artist-film MP4 when it is finished.
// Leave it empty to keep the film section hidden without an empty space.
const ARTIST_FILM_URL = "";
const ARTIST_FILM_CAPTIONS_VTT = ""; // optional English .vtt subtitles

// Optional: use a photo of a REAL World Frame here when you have its URL.
// Until then, the World Frames section uses editorial typography, not a fake image.
const WORLD_FRAME_IMAGE_URL = "";

// Mobile-only decorative painting behind the World Frames title poster.
// Replace this with the Cloudinary URL of an actual World Frame painting when ready.
// Until then, this is a crop of another original Anjaan Vivek painting, NOT labelled as a World Frame.
const WORLD_FRAMES_MOBILE_POSTER_BACKGROUND = "https://res.cloudinary.com/wbwvb4sk/image/upload/v1786278466/elon1.jpg";

// Add official institution logo URLs here only after confirming permission and records.
// When logo is empty, a clean typographic wordmark is shown instead.
const PRESENTATION_VENUES = [
  { name: "Jehangir Art Gallery", location: "Mumbai", logo: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792270670/jag.png" },
  { name: "Lalit Kala Akademi", location: "India", logo: "https://res.cloudinary.com/wbwvb4sk/image/upload/v1792270786/lalit.png" },
  { name: "Bharat Bhavan", location: "Bhopal", logo: "" },
  { name: "Regional Museum of Natural History", location: "Bhopal", logo: "" },
];

const ARTISTIC_ASSOCIATIONS = [
  { name: "PETA", description: "Artistic association and animal-welfare work", logo: "" },
];

// PRE-PUBLISH CHECK: confirm this is the studio WhatsApp number.
// The enquiry form opens WhatsApp with a pre-filled message; it does not send
// data to your website or automatically send messages.
const STUDIO_WHATSAPP_NUMBER = "919300847841";

const ease = [0.22, 1, 0.36, 1];

// If Cloudinary returns 404/403, the browser logs the exact failing URL.
// Animation and network errors can now be distinguished in DevTools.
const reportImageError = (event) => {
  console.warn("[Meet Anjaan] Image failed to load:", event.currentTarget.currentSrc || event.currentTarget.src);
};

function Reveal({ children, className = "", delay = 0, as = "div", media = false }) {
  const reducedMotion = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      className={className}
      // Nothing ever starts at opacity: 0. Images stay visible even if
      // an in-view event is delayed or blocked by a scrolling container.
      initial={reducedMotion ? false : { opacity: media ? 1 : 0.88, y: media ? 18 : 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: reducedMotion ? 0 : 0.9, ease, delay }}
    >
      {children}
    </Component>
  );
}

function Eyebrow({ number, children, light = false }) {
  return (
    <span className={`mav-eyebrow${light ? " mav-eyebrow--light" : ""}`}>
      {number && <span className="mav-eyebrow-number">{number}</span>}
      {children}
    </span>
  );
}

function InstitutionWordmark({ institution }) {
  const [logoUnavailable, setLogoUnavailable] = useState(false);
  // Never substitute a fabricated official logo. For missing logos, use
  // simple initials as an editorial placeholder next to the real name.
  const initials = institution.name
    .split(/\s+/)
    .filter((part) => !["of", "the", "and"].includes(part.toLowerCase()))
    .map((part) => part[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();

  return (
    <div className="mav-institution-item">
      <div className="mav-institution-identity">
        <div className="mav-institution-logo">
          {institution.logo && !logoUnavailable ? (
            <img
              src={institution.logo}
              alt={`${institution.name} logo`}
              loading="lazy"
              decoding="async"
              onError={() => setLogoUnavailable(true)}
            />
          ) : (
            <span className="mav-institution-monogram" aria-hidden="true">
              {initials}
            </span>
          )}
        </div>
        <span className="mav-institution-wordmark">{institution.name}</span>
      </div>
      <span className="mav-institution-location">
        {institution.location || institution.description}
      </span>
    </div>
  );
}


// A light-weight enquiry flow for a static React site. No backend is implied:
// visitors review and send their prepared message in WhatsApp themselves.
function ArtEnquiryForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [regarding, setRegarding] = useState("");
  const [details, setDetails] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!event.currentTarget.reportValidity()) return;
    const message = [
      "Hello Anjaan Vivek Studio,",
      "I would like to connect regarding an artistic project.",
      "",
      `Name: ${name.trim()}`,
      `Contact number: ${phone.trim()}`,
      `Regarding: ${regarding}`,
      details.trim() ? `A little about the enquiry: ${details.trim()}` : null,
    ].filter((line) => line !== null).join("\n");
    const url = `https://wa.me/${STUDIO_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mav-enquiry" id="studio-enquiry" aria-labelledby="mav-enquiry-title">
      <div className="mav-enquiry-inner">
        <div className="mav-enquiry-intro">
          <Eyebrow number="08">A conversation with the studio</Eyebrow>
          <h2 id="mav-enquiry-title">Where art <em>finds its place.</em></h2>
          <p>
            An original work for a space. A life or legacy interpreted on canvas.
            An illustration, a mural, or an idea still taking shape.
            Every collaboration begins with understanding what matters.
          </p>
          <span className="mav-enquiry-small">Selected commissions · Acquisitions · Artistic consultancy</span>
        </div>

        <div className="mav-enquiry-form-shell">
          <span className="mav-enquiry-form-overline">A NOTE TO ANJAAN VIVEK</span>
          <p className="mav-enquiry-form-greeting">
            Hello Anjaan, I would like to connect regarding…
          </p>
          <form className="mav-enquiry-form" onSubmit={handleSubmit}>
            <div className="mav-enquiry-field-row">
              <label className="mav-enquiry-field">
                <span>Your name *</span>
                <input type="text" autoComplete="name" placeholder="Full name"
                  required minLength={2} maxLength={80} value={name}
                  onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="mav-enquiry-field">
                <span>Contact number *</span>
                <input type="tel" autoComplete="tel" inputMode="tel"
                  placeholder="Your phone number" required minLength={7} maxLength={20}
                  value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
            </div>
            <label className="mav-enquiry-field">
              <span>I would like to connect about *</span>
              <select required value={regarding} onChange={(e) => setRegarding(e.target.value)}>
                <option value="" disabled>Select an area of interest</option>
                <option value="Painting / original artwork">Painting / original artwork</option>
                <option value="Pictorial Biography">Pictorial Biography</option>
                <option value="Mural">Mural</option>
                <option value="Illustration">Illustration</option>
                <option value="Art consultancy">Art consultancy</option>
                <option value="Other art involvement">Other art involvement</option>
              </select>
            </label>
            <label className="mav-enquiry-field">
              <span>A few words about your idea <small>(optional)</small></span>
              <textarea rows={3} maxLength={1200} value={details}
                placeholder="Share what you have in mind…"
                onChange={(e) => setDetails(e.target.value)} />
            </label>
            <button className="mav-enquiry-submit" type="submit">
              CONTINUE ON WHATSAPP <span aria-hidden="true">↗</span>
            </button>
            <p className="mav-enquiry-privacy">
              This opens WhatsApp with your message ready for review. Nothing is sent until you choose to send it.
              {" "}<Link to="/contact">Other ways to contact the studio</Link>.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function AboutArtist() {
  const [loading, setLoading] = useState(true);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 95,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // Keep the original visual loader, but show it for only 500 ms.
    const timeout = window.setTimeout(() => setLoading(false), 500);
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <div className="mav-page">
      <AnimatePresence>
        {loading && (
          <motion.div
            className="mav-loader"
            key="mav-loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.2 }}
            aria-hidden="true"
          >
            <span>ANJAAN</span>
            <i />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="mav-scroll-progress"
        style={{ scaleX: smoothProgress }}
        aria-hidden="true"
      />

      <MobileNav />

      <main id="main-content" className="mav-main">
        {/* ACT 00 — A full-screen opening film, bold title at bottom-left. */}
        <section className="mav-hero" aria-labelledby="mav-title">
          <div className="mav-hero-media" aria-hidden="true">
            <video
              className="mav-hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={MEDIA.artistPortrait}
              disablePictureInPicture
            >
              <source src={MEDIA.heroVideo} type="video/mp4" />
            </video>
          </div>
          <div className="mav-hero-shade" />

          <div className="mav-hero-kicker" aria-hidden="true">
            <span>AN ARTIST&apos;S WORLD</span>
            <span>BHOPAL · INDIA</span>
          </div>

          <div className="mav-hero-bottom">
            <div className="mav-hero-heading-wrap">
              <motion.p
                className="mav-hero-small"
                initial={reducedMotion ? false : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                Anjaan Vivek · 45+ years with art
              </motion.p>
              <motion.h1
                id="mav-title"
                className="mav-hero-title"
                initial={reducedMotion ? false : { opacity: 0, y: 65 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease }}
              >
                <span>MEET</span>
                <span>ANJAAN</span>
              </motion.h1>
            </div>
            <a className="mav-hero-scroll" href="#chapter-one">
              <span>SCROLL TO DISCOVER</span>
              <span className="mav-arrow-down" aria-hidden="true">↓</span>
            </a>
          </div>
        </section>

        {/* ACT 01 — A photographic introduction rather than a conventional CV. */}
        <section className="mav-intro" id="chapter-one">
          <div className="mav-intro-topline">
            <Eyebrow number="01">The person behind the work</Eyebrow>
            <span className="mav-intro-aside">VIVEK KUMAR VERMA · ANJAAN VIVEK</span>
          </div>

          <div className="mav-intro-grid">
            <Reveal className="mav-intro-words">
              <h2 className="mav-display-heading">
                WHO IS <em>ANJAAN?</em>
              </h2>
              <p className="mav-intro-lead">
                Some people look at the world.
                <br />
                Artists learn to <em>see</em> it.
              </p>
              <p className="mav-body-copy">
                Anjaan Vivek is a Bhopal-based artist whose practice has grown
                through more than four decades of looking, questioning and making.
                For him, painting is not simply an image; it is a way of
                understanding life.
              </p>
              <p className="mav-body-copy">
                His work moves between figurative and semi-abstract forms,
                allowing each idea to find its own expression.
              </p>
            </Reveal>

            <Reveal media className="mav-portrait-wrap" delay={0.1}>
              <div className="mav-portrait-image">
                <img
                  src={MEDIA.artistPortrait}
                  alt="Portrait of artist Anjaan Vivek"
                  loading="lazy"
                  decoding="async" onError={reportImageError}
                />
              </div>
              <span className="mav-image-credit">ANJAAN VIVEK — THE ARTIST</span>
            </Reveal>
          </div>
          <span className="mav-intro-sideword" aria-hidden="true">THE ARTIST</span>
        </section>

        {/* PHOTOGRAPHIC JOURNAL — lives alongside the paintings, not a services showcase. */}
        <section className="mav-journal" id="artist-journal" aria-labelledby="mav-journal-title">
          <div className="mav-journal-heading">
            <Eyebrow>The artist in his world</Eyebrow>
            <Reveal>
              <h2 id="mav-journal-title">
                Art is a practice.<br />
                <em>And a conversation.</em>
              </h2>
              <p>
                In the studio, an idea begins in silence. In an exhibition,
                it becomes a conversation. Both are part of the same life with art.
              </p>
            </Reveal>
          </div>

          <div className="mav-journal-composition">
            <Reveal media className="mav-journal-main-photo">
              {STORY_PHOTOS.exhibition && (
                <figure className="mav-journal-figure">
                  <img
                    src={STORY_PHOTOS.exhibition}
                    alt="Anjaan Vivek — photograph from the artist's archive"
                    loading="lazy"
                    decoding="async"
                    onError={reportImageError}
                  />
                  <figcaption>01 / Encounters through art</figcaption>
                </figure>
              )}
            </Reveal>

            <div className="mav-journal-aside">
              <Reveal media className="mav-journal-aside-photo" delay={0.08}>
                {STORY_PHOTOS.studio && (
                  <figure className="mav-journal-figure">
                    <img
                      src={STORY_PHOTOS.studio}
                      alt="Anjaan Vivek — a photograph from his artistic practice"
                      loading="lazy"
                      decoding="async"
                      onError={reportImageError}
                    />
                    <figcaption>02 / A life devoted to making</figcaption>
                  </figure>
                )}
              </Reveal>
              <Reveal className="mav-journal-quote" delay={0.12}>
                <span className="mav-journal-rule" aria-hidden="true" />
                <blockquote lang="hi">कला ही अभ्यास है,<br />अभ्यास ही कला है।</blockquote>
                <span className="mav-journal-attribution">— Anjaan Vivek</span>
                <a className="mav-inline-link" href="#studio-enquiry">
                  BEGIN A CONVERSATION <span aria-hidden="true">↗</span>
                </a>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ACT 02 — The paintings take over the viewport. */}
        <section className="mav-art-interlude" aria-label="Anjaan Vivek's artistic practice">
          <img
            className="mav-art-interlude-image"
            src={"https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597543/Udaan_red_1.jpg"}
            alt="Colourful figurative painting by Anjaan Vivek, with sweeping red forms"
            loading="lazy"
            decoding="async" onError={reportImageError}
          />
          <div className="mav-art-interlude-shade" />
          <div className="mav-art-interlude-copy">
            <Eyebrow number="02" light>Art in many forms</Eyebrow>
            <Reveal>
              <h2>
                NEVER
                <br />
                ONE <span>LANGUAGE.</span>
              </h2>
              <p>
                Painting. Portraiture. Landscape. Illustration. Murals.
                Experimentation across materials and forms — with the idea
                always at the centre.
              </p>
            </Reveal>
          </div>
          <span className="mav-art-bottom-caption">DETAIL FROM THE ARTIST&apos;S PRACTICE</span>
        </section>

        {/* ACT 03 — Images are the story; text is a quiet companion. */}
        <section className="mav-practice" id="the-practice">
          <div className="mav-practice-top">
            <Eyebrow number="03">The practice</Eyebrow>
            <Reveal>
              <h2>
                A CANVAS IS
                <br />
                <em>ONLY THE BEGINNING.</em>
              </h2>
            </Reveal>
          </div>
          <div className="mav-practice-composition">
            <Reveal media className="mav-practice-image-one">
              <img
                src={"https://res.cloudinary.com/wbwvb4sk/image/upload/v1791960536/Untitled_design_8.png"}
                alt="Original elephant painting by Anjaan Vivek with blue and green flowing forms"
                loading="lazy"
                decoding="async" onError={reportImageError}
              />
              <span>01 / FORM &amp; COLOUR</span>
            </Reveal>
            <Reveal className="mav-practice-text" delay={0.12}>
              <span className="mav-tiny-divider" />
              <p>
                Across subjects and mediums, the work remains anchored in the
                act of creation — an ongoing conversation between what is felt
                and what takes shape.
              </p>
              <Link to="/allpaintings" className="mav-inline-link">
                EXPLORE THE PAINTINGS <span aria-hidden="true">↗</span>
              </Link>
            </Reveal>
            <Reveal media className="mav-practice-image-two" delay={0.06}>
              <img
                src={"https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597524/Mother_and_Child_-_3_1.jpg"}
                alt="Original painting by Anjaan Vivek, figures gathered within flowing green forms"
                loading="lazy"
                decoding="async" onError={reportImageError}
              />
              <span>02 / MEMORY &amp; EXPERIENCE</span>
            </Reveal>
          </div>
          <div className="mav-project-note">
            <div className="mav-project-note-copy">
              <span className="mav-project-note-label">An artistic practice across forms</span>
              <p>
                After more than four decades in art, the canvas remains central,
                yet the artist&apos;s eye is not bound to one scale or surface.
                Paintings, murals, illustrations and considered commissions are
                approached with the same devotion to form, feeling and meaning.
              </p>
            </div>
            <a href="#studio-enquiry" className="mav-inline-link">
              DISCUSS AN ARTISTIC PROJECT <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>

        {/* ACT 04 — Pictorial Biography and its philosophy, without product cards. */}
        <section className="mav-idea" id="pictorial-biography">
          <div className="mav-idea-header">
            <Eyebrow number="04" light>The idea</Eyebrow>
            <span>AN ORIGINAL ARTISTIC APPROACH</span>
          </div>
          <Reveal>
            <h2 className="mav-idea-title">
              ONE LIFE.
              <br />
              <em>ONE FRAME.</em>
            </h2>
          </Reveal>
          <div className="mav-idea-lower">
            <Reveal className="mav-idea-philosophy">
              <p lang="hi">कर्म प्रधान</p>
              <span>IDENTITY THROUGH ACTIONS</span>
            </Reveal>
            <Reveal className="mav-idea-description" delay={0.15}>
              <h3>Pictorial Biography</h3>
              <p>
                Anjaan Vivek developed Pictorial Biography in a Single Frame to
                interpret an entire life through one uninterrupted composition.
                Childhood, relationships, struggles, decisions and achievements
                become part of a continuous visual journey.
              </p>
              <p>
                A person need not be recognised by a face or a name alone.
                The story of their actions can become their identity.
              </p>
              <p>
                The same visual language can give form to a family's legacy or
                an institution's journey — shaped through conversation,
                research and the artist's interpretation.
              </p>
              <a href="#studio-enquiry" className="mav-inline-link mav-idea-enquire">
                ENQUIRE ABOUT PICTORIAL BIOGRAPHY <span aria-hidden="true">↗</span>
              </a>
            </Reveal>
          </div>
          <div className="mav-idea-backdrop-text" aria-hidden="true">KARMA</div>
        </section>

        {/* ACT 05 — World Frames told through editorial copy, NOT tabs/cards. */}
        <section className="mav-world" id="world-frames">
          <div className="mav-world-intro">
            <Eyebrow number="05">An ongoing series</Eyebrow>
            <Reveal>
              <h2>
                THE WORLD
                <br />
                <em>FRAMES.</em>
              </h2>
            </Reveal>
          </div>
          <div className="mav-world-stage">
            {WORLD_FRAME_IMAGE_URL ? (
              <img
                src={"https://res.cloudinary.com/wbwvb4sk/image/upload/v1786278466/elon1.jpg"}
                alt="An original painting from The World Frames series by Anjaan Vivek"
                loading="lazy"
                decoding="async" onError={reportImageError}
              />
            ) : (
              <div className="mav-world-stage-art" aria-hidden="true">
                <div
                  className="mav-world-mobile-painting"
                  style={{ backgroundImage: `url("https://res.cloudinary.com/wbwvb4sk/image/upload/v1792269662/WhatsApp_Image_2026-09-18_at_6.12.15_PM.jpg")` }}
                />
                <span>THE</span>
                <strong>WORLD</strong>
                <span>FRAMES</span>
              </div>
            )}
            <span className="mav-world-stage-caption">
              {WORLD_FRAME_IMAGE_URL
                ? "THE WORLD FRAMES — ANJAAN VIVEK"
                : "LIVES · ACTIONS · LEGACIES"}
            </span>
          </div>
          <div className="mav-world-end">
            <Reveal>
              <p>
                <strong>The World Frames</strong> is Anjaan Vivek&apos;s continuing
                series of Pictorial Biographies, exploring the journeys of people
                who have shaped the world around us. It has interpreted lives
                including Elon Musk, Sudha Murty, Narendra Modi, Sonu Sood and
                Vladimir Putin — not as conventional portraits, but as stories
                unfolding in a single frame.
              </p>
              <Link to="/worldFrameSeries" className="mav-inline-link">
                ENTER THE WORLD FRAMES <span aria-hidden="true">↗</span>
              </Link>
            </Reveal>
          </div>
        </section>

        {/* ACT 06 — Origin story; short, reflective and human. */}
        <section className="mav-origin">
          <div className="mav-origin-inner">
            <Eyebrow number="06">Where the idea began</Eyebrow>
            <Reveal>
              <h2>
                SOME IDEAS
                <br />
                BEGIN <em>WITHIN.</em>
              </h2>
            </Reveal>
            <Reveal className="mav-origin-content">
              <p>
                During a difficult personal period, Anjaan Vivek found himself
                drawn to stories of resilience and transformation. Those lives
                prompted a question that gradually became a visual language:
              </p>
              <blockquote>
                Could the journey of an entire life be experienced within
                a single painting?
              </blockquote>
            </Reveal>
          </div>
        </section>

        {/* Optional second film: the real interview film appears only when supplied. */}
        {ARTIST_FILM_URL && (
          <section className="mav-film" id="artist-film">
            <div className="mav-film-heading">
              <Eyebrow>In his own words</Eyebrow>
              <h2>THE ARTIST SPEAKS.</h2>
            </div>
            <div className="mav-film-video-wrap">
              <video controls playsInline preload="none" poster={MEDIA.artistPortrait}>
                <source src={ARTIST_FILM_URL} type="video/mp4" />
                {ARTIST_FILM_CAPTIONS_VTT && (
                  <track
                    kind="subtitles"
                    src={ARTIST_FILM_CAPTIONS_VTT}
                    srcLang="en"
                    label="English"
                    default
                  />
                )}
                Your browser does not support HTML5 video.
              </video>
            </div>
          </section>
        )}

        {/* Optional: real installation photographs make a powerful bridge to institutions.
            This entire section is omitted until a real photo is provided above. */}
        {STORY_PHOTOS.installation && (
          <section className="mav-gallery-moment" aria-label="Art in exhibition spaces">
            <img
              src={STORY_PHOTOS.installation}
              alt="Installation view of works by Anjaan Vivek"
              loading="lazy"
              decoding="async"
              onError={reportImageError}
            />
            <div className="mav-gallery-moment-caption">
              <span>THE WORK, IN THE WORLD</span>
              <p>Every encounter gives a painting another life.</p>
            </div>
          </section>
        )}

        {/* SELECTED PRESENTATIONS & ASSOCIATIONS — quiet, editorial credits.
            Confirm each record before publishing; PETA is NOT listed as an exhibition. */}
        <section className="mav-institutions" id="exhibitions-associations">
          <div className="mav-institutions-inner">
            <Eyebrow number="07">Exhibitions &amp; associations</Eyebrow>
            <Reveal>
              <h2 className="mav-institutions-heading">
                ART IN <em>CONVERSATION</em> WITH THE WORLD.
              </h2>
              <p className="mav-institutions-intro">
                From spaces of artistic exchange to enduring associations,
                the work has continued to find new contexts, audiences and meaning.
              </p>
            </Reveal>

            <div className="mav-institutions-group">
              <h3>Selected exhibition &amp; presentation venues</h3>
              <div className="mav-institutions-grid">
                {PRESENTATION_VENUES.map((institution) => (
                  <InstitutionWordmark key={institution.name} institution={institution} />
                ))}
              </div>
            </div>

            <div className="mav-institutions-group mav-institutions-group--association">
              <h3>Artistic associations</h3>
              <div className="mav-institutions-grid mav-institutions-grid--association">
                {ARTISTIC_ASSOCIATIONS.map((institution) => (
                  <InstitutionWordmark key={institution.name} institution={institution} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Discreet direct enquiry, also accessible from the practice and biography sections. */}
        <ArtEnquiryForm />

        {/* ACT 07 — Art House introduced as an extension, not the business pitch. */}
        <section className="mav-house" id="art-house">
          <div className="mav-house-image" aria-hidden="true">
            <img src={"https://res.cloudinary.com/wbwvb4sk/image/upload/v1792269676/WhatsApp_Image_2026-10-06_at_12.36.03_PM.jpg"} alt="" loading="lazy" decoding="async" onError={reportImageError} />
          </div>
          <div className="mav-house-content">
            <Eyebrow number="08">A wider space for art</Eyebrow>
            <Reveal>
              <h2>
                AND THE STORY
                <br />
                <em>CONTINUES.</em>
              </h2>
              <p>
                Guided by the artistic experience of Anjaan Vivek,
                <strong> Anjaan&apos;s Art House</strong> is conceived as a space
                to explore the possibilities of art — from the canvas to the
                different dimensions of life. A place where art is not only seen,
                but experienced.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ACT 08 — The quiet path to real enquiries. */}
        <section className="mav-outro" id="artist-contact">
          <div className="mav-outro-media" aria-hidden="true">
            <img src={MEDIA.redPainting} alt="" loading="lazy" decoding="async" onError={reportImageError} />
          </div>
          <div className="mav-outro-inner">
            <Eyebrow light>ANJAAN VIVEK · THE STUDIO</Eyebrow>
            <Reveal>
              <h2>
                ART BEGINS
                <br />
                A <em>CONVERSATION.</em>
              </h2>
            </Reveal>
            <div className="mav-outro-bottom">
              <p>
                For artistic conversations, acquisitions, commissions and
                collaborations, you can connect with the studio.
              </p>
              <Link to="/contact" className="mav-outro-link">
                CONTACT THE STUDIO <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer1 />
    </div>
  );
}
